import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import moment from 'moment';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { FileUploadModule } from 'primeng/fileupload';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { RippleModule } from 'primeng/ripple';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { ToolbarModule } from 'primeng/toolbar';
import { TooltipModule } from 'primeng/tooltip';
import { Club } from '../../../../model/Club';
import { HttpResponse } from '../../../../model/HttpResponse';
import { Competition } from '../../../../model/competitions/Competition';
import { Registration } from '../../../../model/competitions/Registration';
import { RegistrationStatus } from '../../../../model/enum/RegistrationStatus';
import { RegistrationType } from '../../../../model/enum/RegistrationType';
import { TemporalRegistration } from '../../../../model/enum/TemporalRegistration';
import { ClubService } from '../../../../services/club.service';
import { LoadingService } from '../../../../services/loading.service';
import {
  ExcelReport,
  ReportService,
} from '../../../../services/report.service';

@Component({
  selector: 'app-financial-report',
  standalone: true,
  imports: [
    ChipModule,
    TableModule,
    DialogModule,
    ButtonModule,
    ToastModule,
    ToolbarModule,
    RippleModule,
    ConfirmDialogModule,
    InputTextModule,
    InputTextareaModule,
    CommonModule,
    FileUploadModule,
    DropdownModule,
    TagModule,
    InputTextModule,
    FormsModule,
    InputNumberModule,
    TooltipModule
  ],
  templateUrl: './financial-report.component.html',
  styleUrl: './financial-report.component.scss',
})
export class FinancialReportComponent implements OnInit {
  @Input()
  competition!: Competition;

  @Input()
  registrations: Registration[] = [];

  clubes: Club[] = [];
  clubesRegistred: Club[] = [];
  constructor(
    private clubService: ClubService,
    private loadingService: LoadingService,
    private reportService: ReportService
  ) {}

  ngOnInit() {
    this.getAllClubes();
  }

  getAllClubes() {
    this.loadingService.showLoading();
    this.clubService.getAllClubs().subscribe((res: HttpResponse) => {
      if (res.code !== 200) {
        this.loadingService.hideLoading();
        return;
      }
      this.clubes = res.response;
      this.getClubesRegistred();
    });
  }

  getClubesRegistred() {
    this.clubesRegistred = this.clubes.filter((club) => {
      return this.registrations.some(
        (registration) =>
          registration.clubId === club.id &&
          registration.registrationType === RegistrationType.CLUB_REGISTRATION
          && registration.status === RegistrationStatus.REGISTERED
      );
    });
    this.loadingService.hideLoading();
  }

  exportFinancialReport() {
    const dataToPrint = this.clubesRegistred.map((club: Club) => {
     let itemReport:any = {
        'Club': club.name,
        'Cantidad Ordinarios': this.getRegistrarionByClub(
          club.id!,
          TemporalRegistration.ORDINARY
        ).length,
        'Valor Ordinarios': this.calculateAmountByClub(
          club.id!,
          TemporalRegistration.ORDINARY
        ),
        'Cantidad Extraordinarios': this.getRegistrarionByClub(
          club.id!,
          TemporalRegistration.EXTRAORDINARY
        ).length,
        'Valor Extraordinarios': this.calculateAmountByClub(
          club.id!,
          TemporalRegistration.EXTRAORDINARY
        ),
        'Valor Inscripción Club': this.getClubAmount(club.id!),

      };

      if (this.competition.hasCrosselling) {
        itemReport['Venta Cruzada'] = this.getCrossSellingAmount(club.id!);
        itemReport['Detalle Venta Cruzada'] = this.getCrossSellingDescriptionByClub(club.id!);
      }

      itemReport['Total a pagar'] = this.calculateTotalAmount(club.id!);

      return itemReport;
    });
    const reportName = `Reporte Financiero ${
      this.competition.name
    } a ${moment().format('DD-MM-YYYY')}`;

    const sheets: ExcelReport[] = [
      { data: dataToPrint, sheetName: 'General' },
    ];

    if (this.competition.hasCrosselling) {
      // Sheet: Detalle Venta Cruzada
      const crossSellingDetail: any[] = [];
      for (const reg of this.registrations) {
        if (reg.customData?.crosselingItems) {
          const club = this.clubes.find(c => c.id === reg.clubId);
          const clubName = club ? club.name : reg.clubId;
          for (const item of reg.customData.crosselingItems) {
            crossSellingDetail.push({
              'Club': clubName,
              'Atleta': reg.memberId || '',
              'Item': item.name,
              'Precio Unitario': item.amount,
              'Incluido': item.isIncluded ? 'Si' : 'No',
            });
          }
        }
      }
      sheets.push({ data: crossSellingDetail, sheetName: 'Detalle Venta Cruzada' });

      // Sheet: Resumen Productos
      const productSummary = this.getCrossSellingProductSummary().map(p => ({
        'Producto': p.name,
        'Cantidad Vendida': p.quantity,
        'Precio Unitario': p.unitPrice,
        'Total Recaudado': p.total,
      }));
      sheets.push({ data: productSummary, sheetName: 'Resumen Productos' });
    }

    this.reportService.exportExcel(sheets, reportName);
  }

  getRegistrarionByClub(clubId: string, temporalType: string) {
    return this.registrations.filter((registration) => {
      return (
        registration.clubId === clubId &&
        registration.registrationType ===
          RegistrationType.MEMBER_REGISTRATION &&
        registration.status === RegistrationStatus.REGISTERED &&
        registration.temporalType === temporalType
      );
    });
  }

  calculateAmountByClub(clubId: string, temporalType: string) {
    const registrations = this.getRegistrarionByClub(clubId, temporalType);

    let totalAmount = 0;

    for (const registration of registrations) {
      const athleteAmount = temporalType === TemporalRegistration.ORDINARY
        ? this.competition.registerAmounts.ordinary.athlete
        : this.competition.registerAmounts.extraordinary.athlete;
      
      // Handle both number and EventAmountItem[] formats
      if (typeof athleteAmount === 'number') {
        totalAmount += athleteAmount;
      } else {
        // For EventAmountItem[], we'll use the first amount as default
        // In a real implementation, this would calculate based on the number of events the athlete registered for
        totalAmount += athleteAmount.length > 0 ? athleteAmount[0].amount : 0;
      }
    }

    return totalAmount;
  }

  getClubAmount(clubId: string) {
    const registrationOfClub = this.registrations.find((registration) => {
      return (
        registration.clubId === clubId &&
        registration.registrationType === RegistrationType.CLUB_REGISTRATION
      );
    });

    if (registrationOfClub) {
      return registrationOfClub.temporalType === TemporalRegistration.ORDINARY
        ? this.competition.registerAmounts.ordinary.club
        : this.competition.registerAmounts.extraordinary.club;
    }
    return 0;
  }

  calculateTotalAmount(clubId: string) {

    const totalAmount = 
    this.calculateAmountByClub(clubId, TemporalRegistration.ORDINARY) +
    this.calculateAmountByClub(clubId, TemporalRegistration.EXTRAORDINARY) +
    this.getClubAmount(clubId)

    if(this.competition.hasCrosselling){
      const crossSellingAmount = this.getCrossSellingAmount(clubId);
      return totalAmount + crossSellingAmount;
    }
    
    return totalAmount;
  }

  getCrossSellingAmount(clubId: string) {
    const registrations = this.registrations.filter((registration) => registration.clubId === clubId
    );

    let totalAmount = 0;
    for (const registration of registrations) {
      if (registration.customData && registration.customData.crosselingItems) {
        const crossSellingItems = registration.customData.crosselingItems;
        for (const item of crossSellingItems) {
          if (item.isIncluded) {
            totalAmount += item.amount;
          }
        }
      }
    }
    return totalAmount;
  }

  getCrossSellingDetailByClub(clubId: string): { name: string; quantity: number; unitPrice: number; subtotal: number }[] {
    const detail = new Map<string, { name: string; quantity: number; unitPrice: number; subtotal: number }>();
    for (const reg of this.registrations) {
      if (reg.clubId === clubId && reg.customData?.crosselingItems) {
        for (const item of reg.customData.crosselingItems) {
          if (item.isIncluded) {
            const existing = detail.get(item.name);
            if (existing) {
              existing.quantity++;
              existing.subtotal += item.amount;
            } else {
              detail.set(item.name, { name: item.name, quantity: 1, unitPrice: item.amount, subtotal: item.amount });
            }
          }
        }
      }
    }
    return Array.from(detail.values());
  }

  getCrossSellingProductSummary(): { name: string; quantity: number; unitPrice: number; total: number }[] {
    const summary = new Map<string, { name: string; quantity: number; unitPrice: number; total: number }>();
    for (const reg of this.registrations) {
      if (reg.customData?.crosselingItems) {
        for (const item of reg.customData.crosselingItems) {
          if (item.isIncluded) {
            const existing = summary.get(item.name);
            if (existing) {
              existing.quantity++;
              existing.total += item.amount;
            } else {
              summary.set(item.name, { name: item.name, quantity: 1, unitPrice: item.amount, total: item.amount });
            }
          }
        }
      }
    }
    return Array.from(summary.values());
  }

  getCrossSellingDescriptionByClub(clubId: string): string {
    const detail = this.getCrossSellingDetailByClub(clubId);
    if (detail.length === 0) return '';
    return detail.map(d => `${d.name}(${d.quantity})`).join(', ');
  }
}
